<template>
    <!-- Onglet Historique & fiabilité (remplace « Comparatif 5 mois » et le suivi de précision) -->
    <div v-if="!historique || !precision" class="pp-vide">Chargement de l'historique…</div>
    <div v-else>
        <div class="pp-g2">
            <div class="pp-bloc">
                <div class="pp-bloc__tete">
                    <div class="pp-bloc__titre"><i class="ri-bar-chart-grouped-line"></i>Chiffre d'affaires par mois <small>mois complets, mois en cours projeté</small></div>
                </div>
                <div class="pp-bloc__corps">
                    <apexchart v-if="visible" type="bar" height="280" :options="optionsMois" :series="seriesMois"></apexchart>
                </div>
            </div>
            <div class="pp-bloc">
                <div class="pp-bloc__tete">
                    <div class="pp-bloc__titre"><i class="ri-shield-check-line"></i>Peut-on se fier aux prévisions ?</div>
                </div>
                <div class="pp-bloc__corps">
                    <p v-if="juges.length" class="pp-texte">
                        Sur les {{ juges.length }} derniers mois jugés, la prévision a vu juste (à 10 % près)
                        <b>{{ justes }} fois sur {{ juges.length }}</b>. L'erreur moyenne est de <b>{{ pourcent(precision.mape_moyenne / 100) }}</b>.
                        <span class="pp-txt-m">Chaque prévision est rejouée avec les seules ventes connues avant le mois.</span>
                    </p>
                    <p v-else class="pp-texte pp-txt-m">Pas encore assez d'historique pour juger les prévisions : il faut au moins un mois complet précédé de 30 jours de ventes.</p>
                    <apexchart v-if="visible && juges.length" type="line" height="200" :options="optionsPrecision" :series="seriesPrecision"></apexchart>
                </div>
            </div>
        </div>

        <div class="pp-bloc pp-mt">
            <div class="pp-bloc__tete">
                <div class="pp-bloc__titre"><i class="ri-table-line"></i>Détail mois par mois</div>
                <button type="button" class="ds-btn-ghost !py-1 !px-2 !text-[0.75rem]" @click="exporterCsv"><i class="ri-download-2-line"></i>CSV</button>
            </div>
            <div class="pp-wrap">
                <table class="pp-table">
                    <thead><tr>
                        <th class="th">Mois</th><th class="th pp-r">CA réel</th><th class="th pp-r">Évolution</th>
                        <th class="th pp-r">Prévu</th><th class="th pp-r">Écart</th><th class="th">Verdict</th>
                    </tr></thead>
                    <tbody>
                        <tr v-for="l in lignes" :key="l.cle">
                            <td class="td">{{ l.libelle }}</td>
                            <td class="td pp-r"><b>{{ montant(l.ca) }}</b></td>
                            <td class="td pp-r">
                                <span v-if="l.evolution === null" class="pp-txt-m">-</span>
                                <span v-else :class="l.evolution >= 0 ? 'pp-txt-s' : 'pp-txt-d'">{{ signe(l.evolution) }}{{ pourcent(l.evolution / 100, 1) }}</span>
                            </td>
                            <td class="td pp-r">{{ l.prevu === null ? '-' : montant(l.prevu) }}</td>
                            <td class="td pp-r">{{ l.ecart === null ? '-' : signe(l.ecart) + pourcent(l.ecart / 100, 1) }}</td>
                            <td class="td"><span class="pp-badge" :class="l.verdict[1]">{{ l.verdict[0] }}</span></td>
                        </tr>
                        <tr v-if="enCours" class="pp-encours">
                            <td class="td"><i>{{ moisLibelle(enCours.mmonth, enCours.manne) }}</i><small>{{ enCours.jours_ecoules }} jours sur {{ enCours.jours_mois }}</small></td>
                            <td class="td pp-r">{{ montant(enCours.ca_a_date) }}<small v-if="enCours.projection !== null">projeté {{ montant(enCours.projection) }}</small></td>
                            <td class="td pp-r">
                                <template v-if="enCours.evolution_projetee !== null">
                                    <span :class="enCours.evolution_projetee >= 0 ? 'pp-txt-s' : 'pp-txt-d'">{{ signe(enCours.evolution_projetee) }}{{ pourcent(enCours.evolution_projetee / 100, 1) }}</span><small>projeté</small>
                                </template>
                                <span v-else class="pp-txt-m">-</span>
                            </td>
                            <td class="td pp-r pp-txt-m">-</td><td class="td pp-r pp-txt-m">-</td>
                            <td class="td"><span class="pp-badge pp-b-m">en cours</span></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { montant, montantCourt, pourcent, MOIS_COURTS } from "./appelpp";

export default {
    name: "TabHistoriqueFiabilite",
    components: { apexchart: VueApexCharts },
    props: {
        historique: { type: Object, default: null },   // { datas: [5 mois complets], mois_en_cours }
        precision: { type: Object, default: null },    // { datas: [5 mois], mape_moyenne, nb_periodes }
        // ApexCharts mesure sa largeur au rendu : on ne dessine les graphiques qu'une fois l'onglet affiché
        visible: { type: Boolean, default: false }
    },
    computed: {
        enCours() { return this.historique.mois_en_cours || null; },
        juges() { return (this.precision.datas || []).filter((p) => p.ecart_pct !== null); },
        justes() { return this.juges.filter((p) => Math.abs(p.ecart_pct) <= 10).length; },
        lignes() {
            const prec = {};
            for (const p of this.precision.datas || []) prec[p.periode.slice(0, 7)] = p;
            return (this.historique.datas || []).map((m) => {
                const cle = `${m.manne}-${String(m.mmonth).padStart(2, "0")}`;
                const p = prec[cle];
                const ecart = p && p.ecart_pct !== null ? p.ecart_pct : null;
                let verdict = ["-", "pp-b-m"];
                if (p && p.historique_insuffisant) verdict = ["historique insuffisant", "pp-b-m"];
                else if (ecart !== null) {
                    const a = Math.abs(ecart);
                    verdict = a <= 10 ? ["juste", "pp-b-s"] : a <= 25 ? ["correct", "pp-b-w"] : [ecart > 0 ? "sous-estimé" : "surestimé", "pp-b-d"];
                }
                return {
                    cle, libelle: this.moisLibelle(m.mmonth, m.manne), ca: m.ca_reel, evolution: m.evolution,
                    prevu: p && !p.historique_insuffisant ? p.ca_prevu_cible : null, ecart, verdict
                };
            });
        },
        seriesMois() {
            const d = this.historique.datas || [], e = this.enCours;
            const reste = e && e.projection !== null ? Math.max(0, Math.round(e.projection - e.ca_a_date)) : 0;
            return [
                { name: "CA réel", data: [...d.map((m) => Math.round(m.ca_reel)), e ? Math.round(e.ca_a_date) : 0] },
                { name: "Projection fin de mois", data: [...d.map(() => 0), reste] }
            ];
        },
        optionsMois() {
            const d = this.historique.datas || [], e = this.enCours;
            return {
                chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
                xaxis: { categories: [...d.map((m) => this.moisLibelle(m.mmonth, m.manne)), e ? this.moisLibelle(e.mmonth, e.manne) + " (en cours)" : ""] },
                colors: ["#845ADF", "rgba(132,90,223,0.25)"],
                plotOptions: { bar: { borderRadius: 5, columnWidth: "50%" } },
                yaxis: { labels: { formatter: (v) => montantCourt(v) } },
                dataLabels: { enabled: false },
                legend: { position: "top", horizontalAlign: "right" },
                grid: { borderColor: "#eef1f6" },
                tooltip: { y: { formatter: (v) => montant(v) } }
            };
        },
        seriesPrecision() {
            return [
                { name: "Prévu", data: this.juges.map((p) => Math.round(p.ca_prevu_cible)) },
                { name: "Réel", data: this.juges.map((p) => Math.round(p.ca_reel)) }
            ];
        },
        optionsPrecision() {
            return {
                chart: { type: "line", toolbar: { show: false }, fontFamily: "inherit" },
                xaxis: { categories: this.juges.map((p) => MOIS_COURTS[parseInt(p.periode.slice(5, 7), 10) - 1]) },
                colors: ["#94a3b8", "#845ADF"],
                stroke: { width: [2, 3], dashArray: [5, 0], curve: "smooth" },
                markers: { size: 4 },
                yaxis: { labels: { formatter: (v) => montantCourt(v) } },
                legend: { position: "top", horizontalAlign: "right" },
                grid: { borderColor: "#eef1f6" },
                tooltip: { y: { formatter: (v) => montant(v) } }
            };
        }
    },
    methods: {
        montant, pourcent,
        signe(v) { return v >= 0 ? "+" : ""; },
        moisLibelle(m, a) { return `${MOIS_COURTS[m - 1]} ${a}`; },
        exporterCsv() {
            const lignes = [["Mois", "CA_reel", "Evolution_pct", "Prevu", "Ecart_pct", "Verdict"],
                ...this.lignes.map((l) => [l.libelle, Math.round(l.ca), l.evolution === null ? "" : l.evolution.toFixed(2),
                    l.prevu === null ? "" : Math.round(l.prevu), l.ecart === null ? "" : l.ecart.toFixed(2), l.verdict[0]])];
            const csv = lignes.map((a) => '"' + a.map((v) => String(v ?? "").replace(/"/g, '""')).join('";"') + '"').join("\n");
            const lien = document.createElement("a");
            lien.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
            lien.download = "historique_previsions.csv";
            lien.click();
        }
    }
};
</script>

<style scoped>
.pp-vide { text-align: center; color: #94a3b8; padding: 2.5rem 1rem; }
.pp-g2 { display: grid; grid-template-columns: 3fr 2fr; gap: 1rem; }
.pp-mt { margin-top: 1rem; }
.pp-bloc { border: 1px solid #e2e8f0; border-radius: .625rem; background: #fff; }
.pp-bloc__tete { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: space-between; padding: .8rem 1rem; border-bottom: 1px solid #e2e8f0; }
.pp-bloc__titre { font-weight: 700; color: #0f172a; font-size: .88rem; display: flex; align-items: center; gap: .4rem; }
.pp-bloc__titre i { color: rgb(var(--primary)); }
.pp-bloc__titre small { font-weight: 500; color: #94a3b8; font-size: .72rem; }
.pp-bloc__corps { padding: 1rem; }
.pp-texte { font-size: .85rem; line-height: 1.5; margin: 0 0 .5rem; }
.pp-wrap { overflow-x: auto; }
.pp-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 560px; }
.th { padding: .6rem .8rem; font-weight: 600; font-size: .72rem; color: #1e293b; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; white-space: nowrap; letter-spacing: .02em; text-align: left; }
.td { padding: .6rem .8rem; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-variant-numeric: tabular-nums; color: #1e293b; }
.pp-table tr:hover .td { background: #f8fafc; }
.pp-table .td small { display: block; color: #94a3b8; font-size: .7rem; }
.pp-r { text-align: right !important; }
.pp-encours .td { background: #f8fafc; }
.pp-badge { display: inline-flex; align-items: center; font-size: .68rem; font-weight: 700; padding: .15rem .5rem; border-radius: 99px; white-space: nowrap; }
.pp-b-s { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
.pp-b-w { background: rgb(var(--warning) / .18); color: #9a6c12; }
.pp-b-d { background: rgb(var(--danger) / .12); color: rgb(var(--danger)); }
.pp-b-m { background: #f1f5f9; color: #64748b; }
.pp-txt-s { color: rgb(var(--success)) !important; }
.pp-txt-d { color: rgb(var(--danger)) !important; }
.pp-txt-m { color: #94a3b8 !important; }
@media (max-width: 1000px) { .pp-g2 { grid-template-columns: 1fr; } }
</style>
